// frontend/src/features/viewer/viewerSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { IRootState } from "../app/store"; // <-- adjust path

export type IViewerActiveSearch = "ROOM" | "COMPONENT";

// Tabs in ClickedMcBimItemDetails
export type IViewerMcBimDetailsActiveTab = "identification" | "systems" | "psets";

// How to render details panel
export type IViewerMcBimDetailsLayoutMode = "TABS" | "TABLE";

export type IViewerSelectedSection = "sideA" | "sideB" | "sideC" | "sideD" | "isometric";
export type IViewerSectionCenterCoordinate = [number, number];

export interface IViewerFreeTextSearchState {
  room: string;
  component: string;
  activeSearch: IViewerActiveSearch;
}

export interface IViewerMcBimDetailsState {
  showMmiTrend: boolean;
}

export interface IViewerState {
  freeTextSearch: IViewerFreeTextSearchState;

  // The MongoDB _id (string) of the clicked McBimItem (or null when nothing is selected)
  clickedMcBimItemId: string | null;

  selectedSection: IViewerSelectedSection;
  sectionCenterCoordinate: IViewerSectionCenterCoordinate | null;

  // Details panel settings/state
  mcBimDetails: IViewerMcBimDetailsState;
}

const initialState: IViewerState = {
  freeTextSearch: {
    room: "",
    component: "",
    activeSearch: "ROOM",
  },

  clickedMcBimItemId: null,

  selectedSection: "sideA",
  sectionCenterCoordinate: null,

  mcBimDetails: {
    showMmiTrend: false,
  },
};

export const viewerSlice = createSlice({
  name: "viewer",
  initialState,
  reducers: {
    // -------------------- Search (rooms/components list) --------------------
    setActiveSearchInRedux(state, action: PayloadAction<IViewerActiveSearch>) {
      state.freeTextSearch.activeSearch = action.payload;
    },

    setRoomSearchTextInRedux(state, action: PayloadAction<string>) {
      state.freeTextSearch.room = action.payload;
      state.freeTextSearch.activeSearch = "ROOM";
    },

    setComponentSearchTextInRedux(state, action: PayloadAction<string>) {
      state.freeTextSearch.component = action.payload;
      state.freeTextSearch.activeSearch = "COMPONENT";
    },

    clearRoomSearchInRedux(state) {
      state.freeTextSearch.room = "";
    },

    clearComponentSearchInRedux(state) {
      state.freeTextSearch.component = "";
    },

    clearFreeTextSearchInRedux(state) {
      state.freeTextSearch.room = "";
      state.freeTextSearch.component = "";
      state.freeTextSearch.activeSearch = "ROOM";
    },

    // -------------------- Selection --------------------
    setClickedMcBimItemIdInRedux(state, action: PayloadAction<string | null>) {
      state.clickedMcBimItemId = action.payload;
    },
    clearClickedMcBimItemIdInRedux(state) {
      state.clickedMcBimItemId = null;
    },

    setSelectedSectionInRedux(state, action: PayloadAction<IViewerSelectedSection>) {
      state.selectedSection = action.payload;
    },

    setSectionCenterCoordinateInRedux(
      state,
      action: PayloadAction<IViewerSectionCenterCoordinate | null>
    ) {
      state.sectionCenterCoordinate = action.payload;
    },

    setMcBimDetailsShowMMiTrendInRedux(state, action: PayloadAction<boolean>) {
      state.mcBimDetails.showMmiTrend = action.payload;
    },
  },
});

export const {
  setActiveSearchInRedux,
  setRoomSearchTextInRedux,
  setComponentSearchTextInRedux,
  clearRoomSearchInRedux,
  clearComponentSearchInRedux,
  clearFreeTextSearchInRedux,
  setMcBimDetailsShowMMiTrendInRedux,
  setClickedMcBimItemIdInRedux,
  clearClickedMcBimItemIdInRedux,
  setSelectedSectionInRedux,
  setSectionCenterCoordinateInRedux,
} = viewerSlice.actions;

export const viewerReducer = viewerSlice.reducer;

// -------------------- Selectors --------------------

export const selectViewer = (state: IRootState): IViewerState => state.viewer;

export const selectViewerFreeTextSearch = (state: IRootState) => selectViewer(state).freeTextSearch;

export const selectRoomSearchText = (state: IRootState) => selectViewerFreeTextSearch(state).room;

export const selectComponentSearchText = (state: IRootState) =>
  selectViewerFreeTextSearch(state).component;

export const selectActiveSearch = (state: IRootState) =>
  selectViewerFreeTextSearch(state).activeSearch;

export const selectActiveSearchText = (state: IRootState) => {
  const s = selectViewerFreeTextSearch(state);
  return s.activeSearch === "ROOM" ? s.room : s.component;
};

export const selectClickedMcBimItemId = (state: IRootState) =>
  selectViewer(state).clickedMcBimItemId;

export const selectSelectedSection = (state: IRootState) =>
  selectViewer(state).selectedSection;

export const selectSectionCenterCoordinate = (state: IRootState) =>
  selectViewer(state).sectionCenterCoordinate;

// Details selectors
export const selectMcBimDetails = (state: IRootState) => selectViewer(state).mcBimDetails;

export const selectMcBimDetailsShowMMiTrend = (state: IRootState) =>
  selectMcBimDetails(state).showMmiTrend;
